import {
  forwardRef,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';

import { RiCloseLine } from 'react-icons/ri';

import {
  AutocompleteFactory,
  Box,
  BoxProps,
  Group,
  MantineSize,
  Styles
} from '@mantine/core';

import { ActionIcon } from '../../buttons/actionicon';
import { Badge } from '../../datadisplay';
import { Autocomplete } from '../../inputs';

import { ItemsPickerOverrideClassNames } from './ItemsPicker.styles';

export interface ItemsPickerProps extends BoxProps {
  options: string[];
  value?: string[];
  label?: string;
  description?: string;
  placeholder?: string;
  loading?: boolean;
  size?: MantineSize;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  disabled?: boolean;
  error?: string;
  limit?: number;
  fullWidth?: boolean;
  clearOnBlur?: boolean;
  onChange?: (arr: string[]) => void;
  onAdd?: (item: string) => void;
  onRemove?: (item: string) => void;
  styles?: Styles<AutocompleteFactory>;
  // prop to show dropdown always when focussed
  showOptionsOnFocus?: boolean;
}

const ItemsPicker = forwardRef<HTMLInputElement, ItemsPickerProps>(
  (
    {
      options,
      label,
      description,
      placeholder,
      loading,
      size = 'md',
      leftIcon,
      rightIcon,
      disabled = false,
      value,
      error,
      limit,
      fullWidth,
      clearOnBlur,
      onChange,
      onAdd,
      onRemove,
      showOptionsOnFocus,
      styles,
      ...rest
    },
    ref
  ) => {
    const [selectedItems, setSelectedItems] = useState<string[]>(value ? value : []);
    const [showDropDown, setShowDropdown] = useState(false);

    const classes = useMemo(() => {
      return ItemsPickerOverrideClassNames(size, disabled);
    }, [size, disabled]);

    const filterListOptions = useCallback(
      (currSelectedItems: string[]) => {
        const filteredList = options?.filter(
          (item) => !currSelectedItems.includes(item)
        );
        return filteredList;
      },
      [options]
    );

    const [dropdownList, setDropdownList] = useState<string[]>(
      filterListOptions(value ? value : [])
    );
    const [localValue, setLocalValue] = useState<string>('');

    const itemSelected = (event: string) => {
      setSelectedItems([...selectedItems, event]);
      onAdd?.(event);
    };

    useEffect(() => {
      setDropdownList(filterListOptions(selectedItems));
    }, [selectedItems.length]);

    const removeItem = (currentItem: string) => {
      setSelectedItems(selectedItems.filter((item) => item !== currentItem));
      setDropdownList([...dropdownList, currentItem]);
      onRemove?.(currentItem);
    };

    useEffect(() => {
      if (value) {
        setSelectedItems([...value]);
      }
    }, [value]);

    useEffect(() => {
      onChange?.(selectedItems);
    }, [selectedItems.length]);

    useEffect(() => {
      setDropdownList(filterListOptions(value ?? []));
    }, [options, value]);

    const clearLocalValue = () => {
      if (clearOnBlur) {
        setLocalValue('');
      }
      setShowDropdown(false);
    };

    const handleFocus = () => {
      if (showOptionsOnFocus) {
        setShowDropdown(true);
      }
    };

    useEffect(() => {
      setLocalValue('');
    }, [selectedItems]);

    const dropDownListLength = dropdownList?.length ?? 0;

    return (
      <Box
        {...rest}
        style={{ width: fullWidth ? '100%' : 'auto', position: 'relative' }}
      >
        <Autocomplete
          data={dropdownList}
          loading={loading}
          label={label}
          description={description}
          placeholder={
            selectedItems?.length ? `${selectedItems.length} selected` : placeholder
          }
          leftSection={leftIcon}
          size={size}
          rightSection={rightIcon}
          onOptionSubmit={(event: string) => itemSelected(event)}
          onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
            setLocalValue(e.target.value);
          }}
          value={localValue}
          disabled={disabled}
          error={error}
          limit={
            limit && limit > 0 && limit <= dropDownListLength
              ? limit
              : dropDownListLength
          }
          styles={{
            ...styles
          }}
          ref={ref}
          onBlur={clearLocalValue}
          onFocus={handleFocus}
          dropdownOpened={showOptionsOnFocus ? showDropDown : undefined}
        />
        <Group mt="xs" gap="xs">
          {selectedItems.length
            ? selectedItems.map((item) => (
                <Badge
                  key={item}
                  rightSection={
                    disabled ? null : (
                      <ActionIcon
                        size="xs"
                        variant="transparent"
                        color="secondary"
                        onClick={() => removeItem(item)}
                        aria-label={`Remove ${item}`}
                      >
                        <RiCloseLine size={5} />
                      </ActionIcon>
                    )
                  }
                  variant="light"
                  className={classes.badge}
                >
                  {item}
                </Badge>
              ))
            : null}
        </Group>
      </Box>
    );
  }
);

export { ItemsPicker };
